// src/pages/misc/onboarding/continuousPath.ts
// The live onboarding walks structure, plan, extensions, sign-up, then checkout
// as one path. The older split (structure, then sign-up, then the account pages)
// stays at /onboarding/previo.
//
// [MODULE-MANIFEST] READ THIS FIRST when swapping the landing + public pages + onboarding into another repo.
// Rule for the whole product: every on-screen word is Spanish; every URL is English (English text comes later).
//
// 1) THE TWO ONBOARDING VERSIONS (both start at StructureBuilderPage.tsx, the questions + growing tree)
//    TOGETHER (live):  /onboarding -> /onboarding/plan -> /onboarding/extensiones -> /onboarding/cuenta [SIGNUP-TOGETHER] -> /onboarding/checkout
//    SPLIT (older):    /onboarding/previo -> /signup?listo=1 [SIGNUP-SPLIT] -> logged-in /settings/create-organization/{plan,extensions,checkout}
//    Also exists but is NOT part of the public module: staff hub /onboarding/hub (+ setup-account, create-project, ...), and the logged-in
//    "create another organization" flow (same plan / extensions / checkout components with flow='account').
//
// 2) ROUTES THE MODULE OWNS (App.tsx) and the English names proposed for them. [URL-RENAME] Only a note: nothing is renamed yet.
//    Keep the old Spanish URL as a redirect to the new one so shared links keep working.
//      /como-funciona, /como-funciona/:slug  -> /how-it-works, /how-it-works/:slug
//      /preguntas                            -> /faq
//      /socios                               -> /partners
//      /contacto                             -> /contact   (COLLISION: /contact already exists as ContactStyledPage; decide which page keeps it)
//      /onboarding/previo                    -> /onboarding/split
//      /onboarding/extensiones               -> /onboarding/extensions
//      /onboarding/cuenta                    -> /onboarding/account
//      ?listo=1 -> ?ready=1   ?cuenta=1 -> ?account=1   ?resumen=1 -> ?summary=1   ?tema=socio -> ?topic=partner   #cuestionario -> #quiz
//    Unchanged (already English): /landing, /onboarding, /onboarding/plan, /onboarding/checkout, /signup, /login, /terms, /closing, /join/:token.
//    NOT in this module but Spanish: /cartera (logged-in wallet) and /administrarme (redirect). Rename separately.
//    /login stays exactly as it is.
//
// 3) PLACES OUTSIDE THE MODULE THAT NAME ITS URLS (update them together with any rename):
//      src/App.tsx                              route table
//      src/components/ui/FloatingBot.tsx        HIDDEN_PREFIXES (bot hidden on these pages)
//      src/components/ui/InactivityGuard.tsx    EXCLUDED_PATHS (no auto-logout on these pages)
//      pages/misc/landing/LandingMenu.tsx       menu links (/como-funciona, /socios, /preguntas, /contacto)
//      pages/misc/LandingPage.tsx               navigate('/socios'), navigate('/contacto'), the first-question hand-off to /onboarding
//      pages/misc/public/*                      ArticlePage / ComoFuncionaPage / SociosPage links
//      pages/settings/create-organization/*     navigate('/onboarding?cuenta=1'), '/onboarding/...' strings
//
// 4) THINGS THE MODULE IMPORTS FROM OUTSIDE ITS OWN FOLDER (must exist in the target repo)
//      - pages/settings/create-organization/*   plan, extensions, checkout, orderQuote.ts (price math), orgSetup.ts, orgFlow.css. The PUBLIC
//        onboarding reuses these logged-in files, so they must travel with it. SettingsExperimentalShell is only used by the logged-in version.
//      - pages/auth/auth.css                    styles for the sign-up cards (auth-card, auth-field, ...)
//      - pages/misc/landing/landing.css         .ventaje-landing look and top bar (PublicShell imports it)
//      - contexts/languages/LanguageContext     useTranslation(); keys settings.createOrganization.*, settings.upgrade.*, onboarding.join.*
//      - components/dashboard/ClassicLensBackground   the animated water/lens background mounted once in App.tsx (not per page)
//      - Tailwind CSS classes, the `btn-gold` and `ice-scroll` classes defined in src/index.css, lucide-react icons, react-router-dom v6
//      - supabase client (only SignupPage and JoinInvitePage call it)
//      - LANDING ONLY: components/leads/LeadInitialsBadge.tsx and components/leads/LeadStageBadge.tsx (stagePillStyle). They are used by
//        landing/HeroPreviews.tsx, landing/PipelinePreview.tsx and landing/contrast/VentajeVisuals.tsx for the fake lead cards. Both are
//        brand-new files in the app and must travel with the landing. Also components/dashboard/ClassicLensBackground (see above).
//
// 5) BROWSER STORAGE (the only "database" of the front end). Full descriptions in structureDraft.ts.
//      localStorage: ventaje_draft_v3, ventaje_draft_v3_ready, ventaje_org_setup_v1
//      sessionStorage: ventaje_ob_person, ventaje_s1_done, ventaje_ob_structure_len
//
// 6) WHAT GOES IN AND OUT (the contract, so the module works with any surrounding code structure)
//      IN : nothing. The module is self-contained and starts from the landing quiz / /onboarding.
//      OUT: at /onboarding/checkout the dev reads { structure draft, org setup, person, discount text, orderQuote() } and creates the
//           account + organization + Stripe checkout. See the [HANDOFF] block above CreateOrganizationBillingPage.tsx.
//      PRICING: all math lives in pages/settings/create-organization/orderQuote.ts. Commissionable revenue = quote.plan (seats only).
//
// 7) LANGUAGE STATE. [I18N] Typed-in Spanish (no t()): landing, the builder questions/hints/buttons, public pages, SignupPage.
//    Through translations (es.json + en.json): plan, extensions, account, checkout screens and JoinInvitePage.
import type { PathId } from './structureDraft';

export const LENGTH_KEY = 'ventaje_ob_structure_len';

// [ANIMATION] The four screens after the builder. Their order is the order of the left progress thread. Names are the URL tails
// (/onboarding/<name>). [URL-RENAME] 'extensiones' -> 'extensions', 'cuenta' -> 'account' (also used as keys in publicJourney).
export const PUBLIC_TAIL = ['plan', 'extensiones', 'cuenta', 'checkout'] as const;
export type PublicTail = (typeof PUBLIC_TAIL)[number];

// How many builder screens each path has: path 3 = 8 (S1..S8), path 2 = 7 (no "how many companies"), path 1 = 2 (S1 + SAC), none = 1.
export function structureScreenCount(path: PathId): number {
  if (path === 3) return 8;
  if (path === 2) return 7;
  if (path === 1) return 2;
  return 1;
}

// [ANIMATION] The builder saves its screen count here so the checkout screens can place the progress thread correctly,
// even though the landing quiz may already have answered screen 1 (which removes one screen).
export function rememberStructureLength(count: number): void {
  try {
    sessionStorage.setItem(LENGTH_KEY, String(count));
  } catch {
    /* ignore */
  }
}

// Uses the saved count if it is 2-9, otherwise the default for the path.
function savedStructureLength(path: PathId): number {
  try {
    const count = Number(sessionStorage.getItem(LENGTH_KEY));
    if (count >= 2 && count <= 9) return count;
  } catch {
    /* ignore */
  }
  return structureScreenCount(path);
}

/** Step index and total for a screen after the structure questions. */
export function publicJourney(path: PathId, tail: PublicTail): { step: number; total: number } {
  const structure = savedStructureLength(path);
  const total = structure + PUBLIC_TAIL.length;
  const step = structure + PUBLIC_TAIL.indexOf(tail);
  return { step, total };
}
